import * as React from "react";
import cx from "classnames";
import tw, { styled } from "twin.macro";

import uniqid from "app/lib/uniqid";
import Text from "app/styles/Text";

interface InputFieldContainerProps {
  fullWidth?: boolean;
}

interface Props
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "prefix" | "suffix"
  > {
  containerClassName?: string;
  label?: string;
  secondaryLabel?: React.ReactNode;
  hideLabel?: boolean;
  error?: string;
  description?: React.ReactNode;
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
}

function InputField(props: Props & InputFieldContainerProps, ref: any) {
  const {
    containerClassName,
    label,
    secondaryLabel,
    hideLabel,
    fullWidth,
    error,
    description,
    prefix,
    suffix,
    className,
    id = uniqid(),
    ...rest
  } = props;

  const [showPassword, setShowPassword] = React.useState(false);
  const toggleShowPassword = () => setShowPassword((s) => !s);

  const containerClassNames = cx(containerClassName, {
    "full-width": fullWidth,
  });

  const inputClassName = cx(className, { error });
  const isPasswordType = rest.type === "password";

  return (
    <InputFieldContainer className={containerClassNames}>
      {(label || secondaryLabel) && (
        <LabelBlock
          className={cx({
            spaced: !hideLabel || !!secondaryLabel,
          })}
        >
          <Label htmlFor={id} className={cx({ hide: hideLabel })}>
            {label}
          </Label>
          {secondaryLabel}
        </LabelBlock>
      )}

      {/* Input Wrapper (handles prefix/suffix/password toggle) */}
      <InputWrapper className={cx({ error })}>
        {prefix && <Prefix>{prefix}</Prefix>}

        <Input
          id={id}
          ref={ref}
          className={inputClassName}
          {...rest}
          type={isPasswordType && !showPassword ? "password" : "text"}
        />

        {isPasswordType && (
          <PasswordToggle
            type="button"
            onClick={toggleShowPassword}
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            {showPassword ? <Text medium>Hide</Text> : <Text medium>Show</Text>}
          </PasswordToggle>
        )}

        {suffix && <Suffix>{suffix}</Suffix>}
      </InputWrapper>

      {error && <Text className="error-msg">{error}</Text>}

      {description ? (
        typeof description === "string" ? (
          <Text className="description">{description}</Text>
        ) : (
          description
        )
      ) : null}
    </InputFieldContainer>
  );
}

export default React.forwardRef(InputField);

const InputFieldContainer = styled.div<InputFieldContainerProps>`
  width: 250px;
  max-width: 100%;

  &.full-width {
    width: 100%;
  }

  > p {
    margin-top: 10px;
  }

  > .error-msg {
    ${tw`text-red text-[1.4rem] mt-[4px]`};
  }

  > .description {
    ${tw`text-dark text-[1.4rem] mt-[4px]`};
  }
`;

export const LabelBlock = styled.div`
  ${tw`flex items-center justify-between`};

  &.spaced {
    ${tw`mb-[10px]`};
  }
`;

const Label = styled.label`
  display: block;
  ${tw`text-dark`};
  font-weight: 400;
  font-size: 1.6rem;

  &.hide {
    height: 0px;
    overflow: hidden;
    margin: 0;
  }
`;

const InputWrapper = styled.div`
  ${tw`flex items-center border border-borderMid rounded-[12px] px-[16px] h-[56px] transition-all`};

  &:focus-within {
    ${tw`border-[2px] border-high`};
  }

  &.error {
    ${tw`border-[2px] border-red`};
  }
`;

const Prefix = styled.div`
  ${tw`mr-[8px] text-[1.6rem] text-[#666] flex-shrink-0 flex items-center`};
`;

const Suffix = styled.div`
  ${tw`ml-[8px] text-[1.6rem] text-[#666] flex-shrink-0 flex items-center`};
`;

export const Input = styled.input`
  outline: none;
  width: 100%;
  height: 100%;
  ${tw`bg-containment-tertiary border-none text-[1.6rem] text-text-high`};
  font-family: inherit;
  font-weight: 400;
  padding: 0;

  &::placeholder {
    ${tw`text-[#96A396]  text-[1.6rem] `};
  }

  &:disabled,
  &:read-only {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PasswordToggle = styled.button`
  ${tw`ml-[8px] text-[1.4rem] font-semibold text-dark focus:outline-none`};
`;
